import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { isAttendanceOversightUser } from '../lib/attendanceOversight'
import { attendanceGetAccess, type AttendanceAccess } from '../lib/apiPhpAdapter'
import '../components/RoleSelector.css'

export default function AttendanceSelectRole() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [access, setAccess] = useState<AttendanceAccess | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    attendanceGetAccess()
      .then((a) => {
        if (!cancelled) setAccess(a)
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Nepodařilo se načíst oprávnění')
          const oversight = isAttendanceOversightUser(user)
          setAccess({
            canUser: true,
            canLeader: oversight,
            canPersonalista: oversight || user?.role === 'personalista',
            canAdmin: oversight,
            isItAdmin: user?.role === 'admin' && user?.admin_type === 'spravce_it',
            isOversight: oversight,
          })
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [user])

  const go = (path: string, allowed: boolean, deniedMsg: string) => {
    if (!allowed) {
      window.alert(deniedMsg)
      return
    }
    navigate(path)
  }

  return (
    <div className="role-selector">
      <div className="role-selector-container">
        <button type="button" className="back-to-roles-button" onClick={() => navigate('/intranet')}>
          ← Zpět na intranet
        </button>
        <h1>Docházkový systém</h1>
        <p className="subtitle">Vyberte roli, ve které chcete pokračovat</p>
        {access?.isOversight || isAttendanceOversightUser(user) ? (
          <p className="subtitle" style={{ marginTop: -4 }}>
            Jako vedení školy máte přístup ke všem rolím (správce IT / budov, hlavní ekonom, ředitel).
          </p>
        ) : null}
        {loading ? <p className="subtitle">Načítání oprávnění…</p> : null}
        {error ? (
          <p className="subtitle" style={{ color: '#b45309' }}>
            {error}
          </p>
        ) : null}
        <div className="role-buttons">
          <button
            type="button"
            className="role-button requester"
            onClick={() => go('/attendance/user', !!access?.canUser, 'Nemáte přístup jako uživatel.')}
          >
            <h2>Uživatel</h2>
            <p>Váš přehled docházky</p>
          </button>

          <button
            type="button"
            className="role-button executor"
            style={{ opacity: access && !access.canLeader ? 0.55 : 1 }}
            onClick={() =>
              go(
                '/attendance/leader',
                !!access?.canLeader,
                'Nemáte oprávnění vedoucího docházky. Vedoucí musí být jmenován správcem IT, nebo mít roli vedení školy.',
              )
            }
          >
            <h2>Vedoucí</h2>
            <p>Docházka podřízených zaměstnanců</p>
            {access && !access.canLeader ? (
              <p style={{ marginTop: 8, fontSize: '0.85rem', color: '#888' }}>Nedostupné</p>
            ) : null}
          </button>

          <button
            type="button"
            className="role-button admin"
            style={{ opacity: access && !access.canPersonalista ? 0.55 : 1 }}
            onClick={() =>
              go(
                '/attendance/personalista',
                !!access?.canPersonalista,
                'Přístup mají personalista, správce IT / budov, hlavní ekonom nebo ředitel.',
              )
            }
          >
            <h2>Personalista</h2>
            <p>Všichni zaměstnanci, uzavření období, PDF</p>
            {access && !access.canPersonalista ? (
              <p style={{ marginTop: 8, fontSize: '0.85rem', color: '#888' }}>Nedostupné</p>
            ) : null}
          </button>

          {access?.canAdmin ? (
            <button
              type="button"
              className="role-button admin-it"
              onClick={() => go('/attendance/admin', true, '')}
            >
              <h2>Admin</h2>
              <p>Správa docházky a vedoucích</p>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
